import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Button, Grid } from '@ui-propulsion/react-components';
import { HiwIndicators, HiwStepIndicator } from './helper/hiwStepIndicators';
import { HiwImages, HiwStepImage } from './helper/hiwStepImages';
import { HiwContents, HiwStepContent } from './helper/hiwStepContent';
import HiwControls from './helper/hiwControls';
import styles from './hiwTemplate.module.scss';
import handleCTAClick from '../../services/helpers/handleCTAClick';
import { ctaTheme } from '../../utils/util';

function HiwTemplate({ headline = '', items = [], cta = null }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [startAnimation, setStartAnimation] = useState(false);
  const [ctaThemes, setCtaThemes] = useState(styles.redSolid);
  const hiwRef = useRef(null);

  useEffect(() => {
    setCtaThemes(ctaTheme(cta?.ctaTheme));
  }, [cta]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!startAnimation || isHovered) return;
      setCurrentStep((prevStep) => (prevStep + 1) % items.length);
    }, 4000);
    return () => clearTimeout(timeout);
  }, [currentStep, isHovered, items, startAnimation]);

  const isFullyVisible = (el) => {
    if (!('IntersectionObserver' in window)) {
      return setStartAnimation(true);
    }
    const opts = {
      root: null,
      threshold: 1.0,
    };
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting === true) {
        setStartAnimation(true);
      }
    }, opts);

    return observer.observe(el);
  };

  const initializeAnimation = () => {
    if (!startAnimation) {
      setStartAnimation(true);
    }
  };

  useEffect(() => {
    if (!startAnimation && hiwRef) {
      isFullyVisible(hiwRef.current);
    }
  }, [hiwRef, startAnimation]);

  const handleStepSelection = (step) => {
    if (step !== currentStep) {
      setCurrentStep(step);
    }
  };

  const onHover = (step) => {
    handleStepSelection(step);
    setIsHovered(true);
  };

  const onHoverExit = () => {
    setIsHovered(false);
  };

  const renderSlidesContent = () => {
    let largestLength = 0;
    let largestLengthIndex = -1;

    const contents = items?.map((item, ci) => {
      const slideContentLength =
        item?.title && item?.description
          ? item.title.length + item.description.length
          : 0;

      if (slideContentLength > largestLength) {
        largestLength = slideContentLength;
        largestLengthIndex = ci;
      }

      return (
        <HiwStepContent
          key={`row-${ci}`.toString()}
          isCurrent={currentStep === ci}
          title={item?.title}
          description={item?.description}
          step={ci}
          onClick={() => handleStepSelection(ci)}
          onHover={() => onHover(ci)}
          onHoverExit={onHoverExit}
        />
      );
    });

    if (largestLengthIndex !== -1) {
      contents.push(
        <HiwStepContent
          key="__placeholder"
          isCurrent={false}
          isPlaceholder
          title={items[largestLengthIndex]?.title}
          description={items[largestLengthIndex]?.description}
          step={-1}
          onClick={() => {}}
        />
      );
    }

    return contents;
  };

  return (
    <section
      onMouseEnter={initializeAnimation}
      className={styles.hiwTemplate}
      data-testid="hiw-template"
      ref={hiwRef}
    >
      <Grid container alignItems="center">
        <Grid item xs={12} className={styles.hiwTemplateHeaderTextLayout}>
          <h2 className={styles.hiwTemplateHeaderText}>{headline}</h2>
        </Grid>
        <Grid item xs={12} sm={5} className={styles.hiwTemplateImagesLayout}>
          <div className={styles.hiwTemplateControlsLayout}>
            <HiwImages aria-label="Images describing how it works">
              {items?.map((item, ci) => (
                <HiwStepImage
                  key={`row-${ci}`.toString()}
                  onHover={() => onHover(ci)}
                  onHoverExit={onHoverExit}
                  isCurrent={currentStep === ci}
                  {...item?.image}
                  data-testid={`hiw-step-image-${ci}`.toString()}
                />
              ))}
            </HiwImages>
            <HiwControls
              onLeftClick={() =>
                setCurrentStep(
                  (prevStep) => (prevStep - 1 + items.length) % items.length
                )
              }
              onRightClick={() =>
                setCurrentStep((prevStep) => (prevStep + 1) % items.length)
              }
            />
          </div>
          <HiwIndicators>
            {items?.map((_, ci) => (
              <HiwStepIndicator
                key={`row-${ci}`.toString()}
                isCurrent={currentStep === ci}
                aria-label={`Step ${ci + 1}`}
                onClick={() => setCurrentStep(ci)}
                data-testid={`hiw-step-indicator-${ci}`.toString()}
              />
            ))}
          </HiwIndicators>
        </Grid>
        <Grid item xs={false} sm={1} className={styles.hiwTemplateSpacer} />
        <Grid item xs={12} sm={6} className={styles.hiwTemplateContentsLayout}>
          <h2 className={styles.hiwTemplateHeaderText}>{headline}</h2>
          <HiwContents>{renderSlidesContent()}</HiwContents>

          {cta && (
            <Button
              className={`${styles.hiwTemplateCta} ${ctaThemes}`}
              data-testid="hiw-template-cta"
              fullWidth
              variant="tertiary"
              onClick={(e) => handleCTAClick(e, cta)}
              href={cta.link}
            >
              {cta.label}
            </Button>
          )}
        </Grid>
      </Grid>
    </section>
  );
}

HiwTemplate.propTypes = {
  headline: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string,
      image: PropTypes.shape({
        src: PropTypes.string,
        alt: PropTypes.string,
      }),
    })
  ),
  cta: PropTypes.shape({
    label: PropTypes.string,
    link: PropTypes.string,
  }),
};

export default HiwTemplate;
